import DeleteIcon from '@mui/icons-material/Delete';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../../allauth/auth';
import ApiClient from '../../config/ApiClient';
import { useRoleLabels } from '../../hooks/useRoleLabels';
import {
    AssignmentInvitation,
    PaymentAssignments, ROLE_CONFIG, RoleLabelsByKey, USER_TYPE
} from '../../object-actions/types/types';
import {
    canInviteRole,
    canRemoveCoach,
    canRemoveParent,
    getEditableFields,
    getUserRoleInAssignment,
    type EditableField,
    type UserRole,
} from '../../utils/assignmentPermissions';
import { formatAssignmentPaymentDate } from '../../utils/paymentAssignmentDisplay';
import InviteUserForm from '../invitations/InviteUserForm';
import PendingInvitationsList from '../invitations/PendingInvitationsList';
import ManageAssignmentRoleCard from './ManageAssignmentRoleCard';
import { MANAGE_ASSIGNMENT_ROLE_VISUALS } from './manageAssignmentRoleMedia';

interface ManageAssignmentViewProps {
    assignment: PaymentAssignments;
    isPayer?: boolean;
    onSuccess: () => void;
    onClose: () => void;
}

const ManageAssignmentView: React.FC<ManageAssignmentViewProps> = ({
    assignment,
    isPayer,
    onSuccess,
    onClose,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const auth = useAuth();
    const userId = auth?.data?.user?.id;

    const userRole = isPayer
        ? 'payer'
        : assignment.payment?.entity?.author?.id === userId
            ? 'payer'
            : null;
    const role = getUserRoleInAssignment(userId, assignment, userRole);
    const isSubmitted = !!(
        assignment.pre_assessment_submitted_at ||
        assignment.post_assessment_submitted_at ||
        assignment.pre_assessment_submitted ||
        assignment.post_assessment_submitted
    );
    const editableFields = getEditableFields(role, isSubmitted, {
        athleteAlreadySet: !!assignment.athlete?.id,
    });
    const canInviteAthlete = canInviteRole(role, 'athlete', isSubmitted);
    const canInviteCoach = canInviteRole(role, 'coach', isSubmitted);
    const canInviteGuardian = canInviteRole(role, 'parent', isSubmitted);
    const assignmentId =
        typeof assignment.id === 'number' ? assignment.id : parseInt(String(assignment.id), 10);

    const [pendingInvitations, setPendingInvitations] = useState<AssignmentInvitation[]>(
        assignment.pending_invitations || [],
    );
    const [removingId, setRemovingId] = useState<number | string | null>(null);
    const [invitationsLoading, setInvitationsLoading] = useState(false);

    const refreshInvitations = useCallback(async () => {
        if (!assignmentId) return;
        setInvitationsLoading(true);
        try {
            const res = await ApiClient.get<AssignmentInvitation[]>(
                `/api/payment-assignments/${assignmentId}/invitations/`,
            );
            if (res.success && Array.isArray(res.data)) {
                setPendingInvitations(res.data);
            }
        } finally {
            setInvitationsLoading(false);
        }
    }, [assignmentId]);

    useEffect(() => {
        setPendingInvitations(assignment.pending_invitations || []);
        refreshInvitations();
    }, [assignment, refreshInvitations]);

    const handleInvited = (invitation: AssignmentInvitation) => {
        setPendingInvitations((prev) => [invitation, ...prev]);
        onSuccess();
    };

    const handleRemoveMember = async (
        kind: 'coach' | 'parent',
        memberId: number | string,
        memberName: string,
    ) => {
        const canRemove =
            kind === 'coach'
                ? canRemoveCoach(userId, assignment, memberId, userRole)
                : canRemoveParent(userId, assignment, memberId, userRole);
        if (!canRemove) {
            enqueueSnackbar(`You do not have permission to remove this ${kind}`, { variant: 'error' });
            return;
        }
        setRemovingId(memberId);
        try {
            const updateData: Record<string, number[]> = {};
            if (kind === 'coach') {
                updateData.coaches = (assignment.coaches ?? [])
                    .filter((c) => c.id !== memberId)
                    .map((c) => Number(c.id));
            } else {
                updateData.parents = (assignment.parents ?? [])
                    .filter((p) => p.id !== memberId)
                    .map((p) => Number(p.id));
            }
            const res = await ApiClient.patch(
                `/api/payment-assignments/${assignmentId}/`,
                updateData,
            );
            if (res.success) {
                enqueueSnackbar(`${memberName} removed`, { variant: 'success' });
                onSuccess();
            } else {
                enqueueSnackbar(formatError(res.error), { variant: 'error' });
            }
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Failed to remove member';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setRemovingId(null);
        }
    };

    const athleteAssigned = !!assignment.athlete?.id;
    const paymentDate = formatAssignmentPaymentDate(assignment);

    return (
        <Box>
            <Typography variant="overline" component="h1" fontWeight="bold">
                Manage Assignment
            </Typography>
            <ManageAssignmentContextBar
                role={role}
                editableFields={editableFields}
            />

            <Stack >
                {isSubmitted && (
                    <Alert severity="info">
                        This assignment has a submitted assessment. Existing members are read-only;
                        you can still invite a coach or guardian.
                    </Alert>
                )}

                <Grid container spacing={3} alignItems="stretch">
                    <Grid item xs={12}>
                        <ManageAssignmentRoleCard
                            visual={MANAGE_ASSIGNMENT_ROLE_VISUALS.athlete}
                            headerMinHeight={120}
                        >
                            {athleteAssigned ? (
                                <MemberRow
                                    name={assignment.athlete?.str || ''}
                                    email={assignment.athlete?.entity?.email}
                                    disabled
                                />
                            ) : canInviteAthlete ? (
                                <Stack spacing={1.5}>
                                    <Typography variant="body2" color="text.secondary">
                                        No athlete is attached to this assignment yet.
                                    </Typography>
                                    <InviteUserForm
                                        assignmentId={assignmentId}
                                        role="athlete"
                                        onInvited={handleInvited}
                                    />
                                    <PendingInvitationsList
                                        assignmentId={assignmentId}
                                        invitations={pendingInvitations}
                                        role="athlete"
                                        onChanged={refreshInvitations}
                                    />
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No athlete attached.
                                </Typography>
                            )}
                        </ManageAssignmentRoleCard>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ManageAssignmentRoleCard visual={MANAGE_ASSIGNMENT_ROLE_VISUALS.coach}>
                            <Stack spacing={1.5}>
                                {(assignment.coaches ?? []).length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        No coaches yet.
                                    </Typography>
                                )}
                                {(assignment.coaches ?? []).map((coach) => (
                                    <MemberRow
                                        key={coach.id}
                                        name={coach.str}
                                        email={coach.entity?.email}
                                        onRemove={
                                            editableFields.includes('coaches') &&
                                                canRemoveCoach(userId, assignment, coach.id, userRole)
                                                ? () =>
                                                    handleRemoveMember('coach', coach.id, coach.str)
                                                : undefined
                                        }
                                        removing={removingId === coach.id}
                                    />
                                ))}
                                {canInviteCoach && (
                                    <>
                                        <InviteUserForm
                                            assignmentId={assignmentId}
                                            role="coach"
                                            onInvited={handleInvited}
                                        />
                                        <PendingInvitationsList
                                            assignmentId={assignmentId}
                                            invitations={pendingInvitations}
                                            role="coach"
                                            onChanged={refreshInvitations}
                                        />
                                    </>
                                )}
                            </Stack>
                        </ManageAssignmentRoleCard>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ManageAssignmentRoleCard visual={MANAGE_ASSIGNMENT_ROLE_VISUALS.guardian}>
                            <Stack spacing={1.5}>
                                {(assignment.parents ?? []).map((parent) => (
                                    <MemberRow
                                        key={parent.id}
                                        name={parent.str}
                                        email={parent.entity?.email}
                                        onRemove={
                                            editableFields.includes('parents') &&
                                                canRemoveParent(userId, assignment, parent.id, userRole)
                                                ? () =>
                                                    handleRemoveMember('parent', parent.id, parent.str)
                                                : undefined
                                        }
                                        removing={removingId === parent.id}
                                    />
                                ))}
                                {canInviteGuardian && (
                                    <>
                                        <InviteUserForm
                                            assignmentId={assignmentId}
                                            role="parent"
                                            onInvited={handleInvited}
                                        />
                                        <PendingInvitationsList
                                            assignmentId={assignmentId}
                                            invitations={pendingInvitations}
                                            role="parent"
                                            onChanged={refreshInvitations}
                                        />
                                    </>
                                )}
                            </Stack>
                        </ManageAssignmentRoleCard>
                    </Grid>
                </Grid>

                {!canInviteAthlete && !canInviteCoach && !canInviteGuardian
                    && editableFields.length === 0 && !isSubmitted && (
                    <Alert severity="info">
                        You do not have permission to invite or remove anyone on this assignment.
                    </Alert>
                )}

                {invitationsLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={18} />
                    </Box>
                )}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button onClick={onClose} data-attr="manage-assignment_done">
                    Done
                </Button>
            </Box>
        </Box>
    );
};

const ASSIGNMENT_ROLE_TO_USER_TYPE: Partial<Record<UserRole, USER_TYPE>> = {
    athlete: 'athlete',
    parent: 'parent',
    coach: 'coach',
    payer: 'payer',
};

const FALLBACK_ROLE_LABELS: Record<UserRole, string> = {
    author: 'Author',
    payer: 'Payer',
    parent: 'Guardian',
    athlete: 'Athlete',
    coach: 'Coach',
    none: 'Member',
};

const USER_ROLE_TO_PARTY_KEY: Partial<Record<UserRole, keyof RoleLabelsByKey>> = {
    athlete: 'athlete',
    coach: 'coach',
    parent: 'parent',
};

const EDITABLE_FIELD_PARTY_LABEL: Record<
    EditableField,
    { key: keyof RoleLabelsByKey; plural: boolean }
> = {
    athlete: { key: 'athlete', plural: false },
    coaches: { key: 'coach', plural: true },
    parents: { key: 'parent', plural: true },
};

const ManageAssignmentContextBar: React.FC<{
    role: UserRole;
    editableFields: EditableField[];
}> = ({ role, editableFields }) => {
    const { getRoleLabel } = useRoleLabels();
    const userType = ASSIGNMENT_ROLE_TO_USER_TYPE[role];
    const partyKey = USER_ROLE_TO_PARTY_KEY[role];
    const roleChip = partyKey && userType
        ? {
            label: getRoleLabel(partyKey),
            color: ROLE_CONFIG[userType].color,
        }
        : userType
            ? {
                label: ROLE_CONFIG[userType].label,
                color: ROLE_CONFIG[userType].color,
            }
            : { label: FALLBACK_ROLE_LABELS[role], color: 'default' as const };

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 2 }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 3, mt: 0.5 }}
        >
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase' }}
                >
                    Your role
                </Typography>
                <Chip
                    label={roleChip.label}
                    size="small"
                    color={roleChip.color}
                    variant={role === 'none' ? 'outlined' : 'filled'}
                />
            </Stack>
            {editableFields.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase' }}
                    >
                        Can manage
                    </Typography>
                    {editableFields.map((field) => {
                        const { key, plural } = EDITABLE_FIELD_PARTY_LABEL[field];
                        return (
                            <Chip
                                key={field}
                                label={getRoleLabel(key, { plural })}
                                size="small"
                                variant="outlined"
                            />
                        );
                    })}
                </Stack>
            )}
        </Stack>
    );
};

const MemberRow: React.FC<{
    name: string;
    email?: string | null;
    onRemove?: () => void;
    removing?: boolean;
    disabled?: boolean;
}> = ({ name, email, onRemove, removing, disabled }) => (
    <Paper
        variant="outlined"
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
        }}
    >
        <Chip label={name} size="small" variant="outlined" />
        {email && (
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
                {email}
            </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {onRemove && !disabled && (
            <Tooltip title={`Remove ${name}`}>
                <span>
                    <IconButton size="small" color="error" onClick={onRemove} disabled={removing}>
                        {removing ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                    </IconButton>
                </span>
            </Tooltip>
        )}
    </Paper>
);

function formatError(err: unknown): string {
    if (!err) return 'Request failed';
    if (typeof err === 'string') return err;
    if (typeof err === 'object') {
        const flat = Object.values(err as Record<string, unknown>).flat();
        if (flat.length) return flat.map(String).join('; ');
    }
    return 'Request failed';
}

export default ManageAssignmentView;
